import React, { useState, useEffect } from 'react';
import { Tooltip } from '@mui/material';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { DataProviderInstance } from '../../../../api/DataProvider';
import AreaTitle from '../../../../components/areaTitle/AreaTitle';
import CustomButton from '../../../../components/buttons/CustomButton';
import { useRoleAccess } from '../../../../hooks/useRoleAccess';
import { USER_ROLES } from '../../../../constants/userRoles';
import useSubscriptionFeatures from '../../../../hooks/useSubscriptionFeatures';
import { SUBSCRIPTION_LIMITS } from '../../../../consts/SubscriptionFeatures';
import FeatureLockBanner from '../../../../components/subscription/FeatureLockBanner';
import PlanUpgradeModal from '../../../../components/subscription/PlanUpgradeModal';
import useCurrentPlanLabel from '../../../../components/subscription/useCurrentPlanLabel';
import { limitActionHint, planLimitText } from '../../../../components/subscription/planLockTexts';
import ConfirmPopup from '../../../../popup/ConfirmPopup/ConfirmPopup';
import Toast from '../../../../components/support/Toast';
import RoomListCard from './components/RoomListCard';
import RoomFormPopup from './components/RoomFormPopup';
import RoomDeletePopup from './components/RoomDeletePopup';
import styles from './CompanyRooms.module.css';

const byName = (a, b) => String(a.name).localeCompare(String(b.name), 'pt-BR', { sensitivity: 'base' });

// O DataProvider já busca as salas de novo depois de salvar (para o resto do app);
// a página só ajusta a própria lista, sem uma segunda busca.
const savedRoomId = (result) => result?.data?.id ?? result?.id;

const EMPTY_FORM = {
    name: '',
    description: '',
    capacity: 1,
    is_active: true
};

/**
 * Salas da empresa (/company/rooms): orquestra lista, formulário, ativar/inativar e excluir.
 * O limite do plano conta só salas ativas e barra criar e ativar antes da API.
 */
const CompanyRooms = () => {
    const dataProvider = DataProviderInstance();
    const { GetCompanyRooms, SetCompanyRoom, UpdateCompanyRoom, DeleteCompanyRoom, GetRoomsOccupancyBatch } = dataProvider;
    const { hasRole } = useRoleAccess();
    const { getLimit } = useSubscriptionFeatures();
    const planLabel = useCurrentPlanLabel();

    // Só proprietário e administradores editam
    const canEdit = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);

    // Aviso de limite do plano: false | 'create' | 'reactivate' (muda o que fazer no texto)
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [rooms, setRooms] = useState([]);
    const [occupancyData, setOccupancyData] = useState({});
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingRoom, setEditingRoom] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [localLoading, setLocalLoading] = useState(false);
    // Erro de excluir ou ativar/inativar, mostrado na tela (sem alert do navegador)
    const [pageError, setPageError] = useState('');
    // Sala esperando a confirmação de inativar
    const [inactivateConfirm, setInactivateConfirm] = useState(null);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const showToast = (message, severity = 'success') => {
        setToast({ open: true, message, severity });
    };

    // Troca ou acrescenta a sala na lista local (mantém a ordem por nome do servidor)
    const upsertRoom = (room) => {
        setRooms((prev) => {
            const exists = prev.some((item) => String(item.id) === String(room.id));
            const next = exists
                ? prev.map((item) => (String(item.id) === String(room.id) ? { ...item, ...room } : item))
                : [...prev, room];
            return next.sort(byName);
        });
        setOccupancyData((prev) => ({
            ...prev,
            [room.id]: { current_occupancy: prev[room.id]?.current_occupancy || 0, capacity: room.capacity }
        }));
    };

    const loadRooms = () => {
        setLoading(true);
        GetCompanyRooms((result) => {
            if (result && !result.error) {
                setRooms(result);
                loadOccupancyData(result);
            }
            setLoading(false);
        });
    };

    // Atendimentos de hoje em cada sala. O servidor usa a data do início e o horário
    // de início e de fim no mesmo dia: a janela é o dia inteiro (00:00 a 23:59), não
    // "agora + 24 h", que virava um instante (mesmo H:i nas duas pontas) e dava sempre 0.
    const loadOccupancyData = (roomsList) => {
        if (roomsList.length === 0) return;

        const dayStart = new Date();
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayStart);
        dayEnd.setHours(23, 59, 0, 0);
        const startDateTime = dayStart.toISOString();
        const endDateTime = dayEnd.toISOString();

        const roomIds = roomsList.map(room => room.id);
        GetRoomsOccupancyBatch(roomIds, startDateTime, endDateTime, (result) => {
            if (result && !result.error) {
                const occupancyMap = {};
                result.forEach(item => {
                    occupancyMap[item.room_id] = {
                        current_occupancy: item.current_occupancy,
                        capacity: item.capacity
                    };
                });
                setOccupancyData(occupancyMap);
            }
        });
    };

    useEffect(() => {
        loadRooms();
    }, []);

    // Limite do plano: mesma conta do servidor, só salas ativas ocupam o limite
    const roomLimit = getLimit(SUBSCRIPTION_LIMITS.ROOMS);
    const activeRoomsCount = rooms.filter((room) => room.is_active).length;
    const isAtRoomLimit = roomLimit !== null && activeRoomsCount >= roomLimit;
    const roomLimitMessage = isAtRoomLimit
        ? `${planLimitText(planLabel, roomLimit, 'sala', 'salas')}. ${limitActionHint(true)}`
        : '';
    const reactivateLimitMessage = isAtRoomLimit
        ? `${planLimitText(planLabel, roomLimit, 'sala ativa', 'salas ativas')}. ${limitActionHint(true, true)}`
        : '';

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Nome da sala é obrigatório';
        } else if (formData.name.trim().length < 2) {
            newErrors.name = 'Nome deve ter pelo menos 2 caracteres';
        } else if (formData.name.trim().length > 50) {
            newErrors.name = 'Nome deve ter no máximo 50 caracteres';
        }

        // Nome repetido (exceto a própria sala em edição)
        const existingRoom = rooms.find(room =>
            room.name.toLowerCase() === formData.name.trim().toLowerCase() &&
            room.id !== editingRoom?.id
        );
        if (existingRoom) {
            newErrors.name = 'Já existe uma sala com este nome';
        }

        if (formData.description && formData.description.length > 200) {
            newErrors.description = 'Descrição deve ter no máximo 200 caracteres';
        }

        // Todos os campos com problema aparecem juntos (não para no primeiro erro)
        const capacity = Number(formData.capacity);
        if (formData.capacity === '' || !Number.isInteger(capacity) || capacity < 1) {
            newErrors.capacity = 'Capacidade deve ser pelo menos 1';
        } else if (capacity > 100) {
            newErrors.capacity = 'Capacidade deve ser no máximo 100';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const resetForm = () => {
        setFormData(EMPTY_FORM);
        setEditingRoom(null);
        setShowForm(false);
        setErrors({});
    };

    const handleSubmit = (e) => {
        if (e && e.preventDefault) e.preventDefault();

        if (!validateForm()) return;

        // Ativar uma sala inativa pelo formulário conta no limite, como ativar pelo botão
        if (editingRoom && !editingRoom.is_active && formData.is_active && isAtRoomLimit) {
            setUpgradeModalOpen('reactivate');
            return;
        }

        setLocalLoading(true);
        const roomData = {
            name: formData.name.trim(),
            description: formData.description.trim(),
            capacity: parseInt(formData.capacity),
            is_active: formData.is_active
        };

        if (editingRoom) {
            UpdateCompanyRoom({ ...roomData, id: editingRoom.id }, (result) => {
                if (result && !result.error) {
                    upsertRoom({ ...editingRoom, ...roomData });
                    resetForm();
                    showToast('Sala atualizada');
                } else if (result?.code === 'BLOCKED_ON_PLAN') {
                    // O servidor também barra no limite (ex.: outra aba ativou antes)
                    loadRooms();
                    resetForm();
                    setUpgradeModalOpen('reactivate');
                } else {
                    setErrors({ general: result?.error || 'Erro ao atualizar sala' });
                }
                setLocalLoading(false);
            });
        } else {
            SetCompanyRoom(roomData, (result) => {
                if (result && !result.error) {
                    const newId = savedRoomId(result);
                    if (newId !== undefined && newId !== null) {
                        upsertRoom({ ...roomData, id: newId });
                    } else {
                        loadRooms();
                    }
                    resetForm();
                    showToast('Sala criada');
                } else if (result?.code === 'BLOCKED_ON_PLAN') {
                    // O servidor também barra no limite (ex.: outra aba criou antes)
                    loadRooms();
                    resetForm();
                    setUpgradeModalOpen('create');
                } else {
                    setErrors({ general: result?.error || 'Erro ao criar sala' });
                }
                setLocalLoading(false);
            });
        }
    };

    const startEdit = (room) => {
        setFormData({
            name: room.name,
            description: room.description || '',
            capacity: room.capacity,
            is_active: room.is_active
        });
        setEditingRoom(room);
        setShowForm(true);
        setErrors({});
    };

    const handleDelete = () => {
        if (!deleteConfirm) return;

        setLocalLoading(true);
        setPageError('');
        DeleteCompanyRoom(deleteConfirm.id, (result) => {
            if (result && !result.error) {
                const deletedId = String(deleteConfirm.id);
                setRooms((prev) => prev.filter((room) => String(room.id) !== deletedId));
                showToast('Sala excluída');
            } else {
                setPageError(result?.error || 'Não foi possível excluir a sala. Tente de novo.');
            }
            setDeleteConfirm(null);
            setLocalLoading(false);
        });
    };

    // Inativar pede confirmação (como em Agendas e Procedimentos); ativar segue direto
    const handleToggleClick = (room) => {
        if (room.is_active) {
            setInactivateConfirm(room);
        } else {
            toggleRoomStatus(room);
        }
    };

    const toggleRoomStatus = (room) => {
        // Ativar no limite do plano: explica e oferece a troca antes de chamar o servidor
        if (!room.is_active && isAtRoomLimit) {
            setUpgradeModalOpen('reactivate');
            return;
        }
        setLocalLoading(true);
        setPageError('');
        const updatedRoom = {
            ...room,
            is_active: !room.is_active
        };

        UpdateCompanyRoom(updatedRoom, (result) => {
            if (result && !result.error) {
                upsertRoom(updatedRoom);
                showToast(updatedRoom.is_active ? 'Sala ativada' : 'Sala inativada');
            } else if (result?.code === 'BLOCKED_ON_PLAN') {
                loadRooms();
                setUpgradeModalOpen('reactivate');
            } else {
                setPageError(result?.error || 'Não foi possível mudar o status da sala. Tente de novo.');
            }
            setLocalLoading(false);
        });
    };

    // Nova sala: no limite abre o aviso de troca de plano, senão o formulário
    const handleAddRoom = () => {
        if (isAtRoomLimit) {
            setUpgradeModalOpen('create');
        } else {
            setShowForm(true);
        }
    };

    return (
        <div className={`page-content ${styles.mainContainer}`}>
            <PlanUpgradeModal
                isOpen={!!upgradeModalOpen}
                onClose={() => setUpgradeModalOpen(false)}
                featureName="salas"
                featureNameSingular="sala"
                type="limit"
                limit={roomLimit}
                hint={limitActionHint(true, upgradeModalOpen === 'reactivate')}
            />

            <div className={styles.headerContainer}>
                <AreaTitle
                    Title="Salas"
                    Icon={<MeetingRoomOutlinedIcon />}
                    Description="Os espaços onde os atendimentos acontecem, com capacidade e ocupação."
                />
                <Tooltip
                    title={
                        !canEdit
                            ? 'Apenas proprietários e administradores podem gerenciar salas'
                            : roomLimitMessage
                    }
                    arrow
                >
                    <span>
                        <CustomButton
                            style="main"
                            variant="contained"
                            icon={<AddRoundedIcon />}
                            label="Nova sala"
                            onClick={handleAddRoom}
                            enabled={canEdit}
                        />
                    </span>
                </Tooltip>
            </div>

            {isAtRoomLimit && (
                <FeatureLockBanner
                    type="limit"
                    lockIcon
                    featureName="salas"
                    featureNameSingular="sala"
                    currentCount={activeRoomsCount}
                    limit={roomLimit}
                    hint={limitActionHint(true)}
                    onUpgradeClick={() => setUpgradeModalOpen('create')}
                />
            )}

            {pageError && (
                <div className={styles.errorMessage} role="alert">{pageError}</div>
            )}

            <RoomListCard
                loading={loading && rooms.length === 0}
                rooms={rooms}
                occupancyData={occupancyData}
                canEdit={canEdit}
                busy={localLoading}
                reactivateHint={reactivateLimitMessage}
                onAdd={handleAddRoom}
                onEdit={startEdit}
                onToggle={handleToggleClick}
                onDelete={setDeleteConfirm}
            />

            {showForm && (
                <RoomFormPopup
                    isEdit={!!editingRoom}
                    formData={formData}
                    errors={errors}
                    saving={localLoading}
                    onFieldChange={(name, value) => {
                        setFormData((prev) => ({ ...prev, [name]: value }));
                        // Corrigiu o campo: some o aviso dele, os outros continuam
                        setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
                    }}
                    onSubmit={handleSubmit}
                    onClose={resetForm}
                />
            )}

            {deleteConfirm && (
                <RoomDeletePopup
                    room={deleteConfirm}
                    saving={localLoading}
                    onConfirm={handleDelete}
                    onClose={() => setDeleteConfirm(null)}
                />
            )}

            <ConfirmPopup
                Visibility={!!inactivateConfirm}
                Title="Inativar sala"
                Description={`A sala "${inactivateConfirm?.name || ''}" sai das opções na hora de marcar, mas o histórico continua. Dá para ativar depois.`}
                ConfirmLabel="Inativar"
                OnConfirm={() => toggleRoomStatus(inactivateConfirm)}
                OnHide={() => setInactivateConfirm(null)}
            />

            <Toast
                open={toast.open}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                message={toast.message}
                severity={toast.severity}
                autoHideDuration={6000}
            />
        </div>
    );
};

export default CompanyRooms;
